import Card from 'components/Layout/Card';
import Loader from 'components/Layout/Loader';
import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getMentorshipStatisticsChartData } from 'store/slices';
import { objectToFormData } from 'utils/helpers';
import CalendarFilterDropdown from 'components/Layout/CalendarFilterDropdown/CalendarFilterDropdown';

const MentorshipStatisticsChart = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const fetchMentorshipStatisticsChart = async (searchData) => {
    setIsLoading(true);
    const updatedSearchData = Object.fromEntries(
      Object.entries(searchData).map(([key, value]) =>
        key === 'join_date' ? ['date_filter', value] : [key, value]
      )
    );

    const response = await dispatch(
      getMentorshipStatisticsChartData(objectToFormData(updatedSearchData))
    );

    if (response.status === 200) {
      setChartData(response?.data);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchMentorshipStatisticsChart({ date_filter: 'Last 24 Hours' });
  }, []);

  const [searchObject, setSearchObject] = useState({});
  const [timer, setTimer] = useState('');

  const searchInputChange = (searchData) => {
    fetchMentorshipStatisticsChart(searchData);
  };

  const handelChangeInput = (e) => {
    let name = e.target.id;
    let value = e.target.value;
    let newPayload = '';
    if (e.target.value === 'custom') {
      newPayload = {
        ...searchObject,
        [name]: value,
        start_date: e.target.start_date,
        end_date: e.target.end_date,
      };
      setSearchObject(newPayload);
    } else {
      if (searchObject?.start_date && searchObject?.end_date) {
        const { start_date, end_date, ...newSearchObject } = searchObject;
        setSearchObject({ ...newSearchObject, [name]: value });
      } else {
        setSearchObject({ ...searchObject, [name]: value });
      }
    }

    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      if (e.target.value === 'custom') {
        searchInputChange(newPayload);
      } else {
        if (searchObject?.start_date && searchObject?.end_date) {
          const { start_date, end_date, ...newSearchObject } = searchObject;
          searchInputChange({ ...newSearchObject, [name]: value });
        } else {
          searchInputChange({ ...searchObject, [name]: value });
        }
      }
    }, 800);
    setTimer(time);
  };

  return (
    <div className="cmt-20">
      <Card className="cpt-26 cpb-40 cps-53 cpe-53">
        <div className="d-flex justify-content-end cmb-10">
          <CalendarFilterDropdown
            onChange={handelChangeInput}
            elem={{ title: 'Join Date' }}
          />
        </div>

        {isLoading ? (
          <>
            <span className="">
              <Loader size="sm" />
            </span>
          </>
        ) : (
          <div className="row">
            <div className="col-md-6">
              <div className="text-20-500-25 color-1717 cmb-38">
                Mentorship Statistics
              </div>
              <div className="d-flex align-items-center gap-3 cmb-23">
                <div className="rounded-color bg-5110"></div>
                <div className="text-16-400-20 color-0000 min-width-250">
                  Mentor Approvals Pending
                </div>
                <div className="text-16-400-20 color-0000">
                  : {chartData?.mentor_approvals_panding || 0}
                </div>
                <div
                  className="color-7cff text-14-400-18 pointer"
                  onClick={() => {
                    navigate(`/admin/mentorship-management/approval`);
                  }}
                >
                  View Details
                </div>
              </div>

              <div className="d-flex align-items-center gap-3 cmb-23">
                <div className="rounded-color bg-C991"></div>
                <div className="text-16-400-20 color-0000 min-width-250">
                  Recently Created Sessions
                </div>
                <div className="text-16-400-20 color-0000">
                  : {chartData?.recently_created_sessions || 0}
                </div>
                <div
                  className="color-7cff text-14-400-18 pointer"
                  onClick={() => {
                    navigate(`/admin/mentorship-management/approval`);
                  }}
                >
                  View Details
                </div>
              </div>

              <div className="d-flex align-items-center gap-3 cmb-24">
                <div className="rounded-color bg-a35b"></div>
                <div className="text-16-400-20 color-0000 min-width-250">
                  Session Approvals Pending
                </div>
                <div className="text-16-400-20 color-0000">
                  : {chartData?.session_approvals_panding || 0}
                </div>
                <div
                  className="color-7cff text-14-400-18 pointer"
                  onClick={() => {
                    navigate(`/admin/mentorship-management/approval`);
                  }}
                >
                  View Details
                </div>
              </div>

              <div className="d-flex align-items-center gap-3 text-20-500-25 color-1717 cps-38">
                <div className="">Total New Mentors</div>
                <div className="">: {chartData?.total_mentor || 0}</div>
              </div>
            </div>
            <div className="col-md-6 h-100">
              <div
                className="flex justify-center items-center"
                style={{
                  // height: "500px", // Increase container height
                  width: '100%', // Increase container width
                  overflow: 'visible', // Allow labels to render outside the chart
                }}
              >
                <ReactApexChart
                  options={{
                    labels: [
                      'Mentor Approvals Pending',
                      'Recently Created Sessions',
                      'Session Approvals Pending',
                    ],
                    dataLabels: {
                      enabled: true,
                      formatter: function (val, opts) {
                        // Combine count and percentage
                        const count = opts.w.globals.series[opts.seriesIndex];
                        return `${count}\n(${val.toFixed(1)}%)`;
                      },
                      style: {
                        fontSize: '14px',
                        fontWeight: 'bold',
                        colors: ['#000000'], 
                      },
                      dropShadow: {
                        enabled: false, // Disable shadow for cleaner text
                      },
                    },
                    plotOptions: {
                      pie: {
                        customScale: 0.9,
                        dataLabels: {
                          offset: 80, // Move data labels outside the chart slice
                          minAngleToShowLabel: 15, // Ensures labels are displayed for smaller slices
                        },
                        expandOnClick: true,
                        donut: {
                          size: '50%',
                          labels: {
                            show: true,
                            total: {
                              show: true,
                              showAlways: true,
                              label: 'Total',
                              fontSize: '20px',
                              lineHeight: '25px',
                              fontWeight: 500,
                              color: '#171717',
                              offsetY: 20,
                              formatter: function (w) {
                                // Calculate and display the total count
                                return w.globals.seriesTotals.reduce(
                                  (a, b) => a + b,
                                  0
                                );
                              },
                            },
                          },
                        },
                      },
                    },
                    grid: {
                      padding: {
                        top: 10, // Adjust top padding for more plot area
                        bottom: 10, // Adjust bottom padding
                      },
                    },
                    colors: ['#E75110', '#C99100', '#2BA35B'],
                    stroke: {
                      width: 0, // Remove borders for a cleaner look
                    },
                    legend: { show: false }, // Hide legend
                  }}
                  series={[
                    chartData?.mentor_approvals_panding || 0,
                    chartData?.recently_created_sessions || 0,
                    chartData?.session_approvals_panding || 0,
                  ]}
                  type="donut"
                  height={350}
                />
              </div>
            </div>

            {/* <div className="col-md-6">
            <ReactApexChart
              options={{
                labels: [
                  "Mentor Approvals Pending",
                  "Recently Created Sessions",
                  "Session Approvals Pending",
                ],
                dataLabels: {
                  enabled: true,
                  formatter: function (val) {
                    return val.toFixed(2) + "%";
                  },
                  style: {
                    fontSize: "12px",
                    colors: ["#000000"], // Color for the data labels
                  },
                //   pie: {
                //     offset: 20, // Adjust the position of the labels outside
                //     dataLabels: {
                //       offset: -50, // Additional offset to move labels further outside
                //     },
                //   },
                },

                plotOptions: {
                  pie: {
                    expandOnClick: true,
                    donut: {
                      size: "50%",
                      labels: {
                        show: true,
                        total: {
                          show: true,
                          showAlways: true,
                          label: "Total",
                          fontSize: "20px",
                          lineHeight: "25px",
                          fontWeight: 500,
                          color: "#171717",
                          offsetY: 20,
                          formatter: function (w) {
                            return total_mentor;
                          },
                        },
                      },
                    },
                  },
                },
                colors: ["#E75110", "#C99100", "#2BA35B"],

                stroke: {
                  curve: "smooth",
                  width: 3,
                },
                xaxis: {
                  categories: [
                    "Mentor Approvals Pending",
                    "Recently Created Sessions",
                    "Session Approvals Pending",
                  ],
                },

                legend: { show: false },
              }}
              series={[
                mentor_approvals_panding || 0,
                recently_created_sessions || 0,
                session_approvals_panding || 0,
              ]}
              type="donut"
              height={320}
            />
          </div> */}
          </div>
        )}
      </Card>
    </div>
  );
};

export default MentorshipStatisticsChart;
